import classnames from 'classnames';
import styles from './subscription.module.scss';
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { GenericObject, RootState } from 'v2/models';
import { Redirect } from 'react-router-dom';
import {
  PERMISSION_NAME,
  getPermissionData,
  hasActivePermissions,
  hasPlanFamily,
} from 'v2/utils/permissions';
import SubscriptionManagement from './SubscriptionManagement';
import SubscriptionUpsellEnterprise from './SubscriptionUpsellEnterprise';
import { useAppDispatch } from 'store';
import {
  fetchCheckoutNew,
  fetchPermissionsAction,
  fetchSubscriptionPortal,
  fetchUserObject,
} from 'actions';
import SignUpSubscription from './SignUpSubscription';
import { useFeatureFlags } from 'v2/hooks/useFeatureFlags';
import { FEATURE_FLAGS } from 'utils/constants';
import { useIsConnectUser } from 'v2/hooks/useIsConnectUser';

const FETCH_USER_INTERVAL = 5000;
const FETCH_SUBSCRIPTION_RETRIES = 15;

const Subscription = () => {
  const [isUpsellConnectEnabled] = useFeatureFlags([
    FEATURE_FLAGS.UPSELL_CONNECT_USER_SUBSCRIPTIONS,
  ]);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state: RootState) => state.user.user);
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );
  const permissionList = getPermissionData(permissions, [
    PERMISSION_NAME.CREATE_SEARCH,
  ]);
  const isConnectUser = useIsConnectUser();
  const hasNoLicenses =
    permissionList.length && !permissionList[0].subscription_chargebee_id;
  const chargebeeInstance = useRef<GenericObject>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    chargebeeInstance.current = window.Chargebee.init({
      site: process.env.REACT_APP_CHARGEBEE_SITE,
    });
  }, []);

  const propForSubscription = (interval: number, retryCount: number) => {
    const performProbe = async (retryNum: number) => {
      await fetchUserObject();
      await fetchPermissionsAction()(dispatch);

      if (
        permissionList.length &&
        !permissionList[0].subscription_id &&
        retryNum < retryCount
      ) {
        setTimeout(() => performProbe(retryNum + 1), interval);
      } else {
        setLoading(false);
      }
    };
    performProbe(0);
  };

  const handleCheckout = () => {
    let success = false;
    setLoading(true);
    chargebeeInstance.current?.openCheckout({
      hostedPage: () => {
        return fetchCheckoutNew().then((response: GenericObject) => response);
      },
      success: () => {
        success = true;
        propForSubscription(FETCH_USER_INTERVAL, FETCH_SUBSCRIPTION_RETRIES);
      },
      close: () => {
        // This is to avoid the scenario where a users hit the x button rather than close to exit
        // the subscription dialog after they signed up and the front-end is still waiting for
        // notification of a successful signup. Without it they could sign up for a second subscription
        if (!success) {
          setLoading(true);
        }
      },
    });
  };

  const handleSubPortal = () => {
    const cb = chargebeeInstance.current;
    cb?.setPortalSession(fetchSubscriptionPortal);
    const cbPortal = cb?.createChargebeePortal();
    cbPortal.open({
      close: () => {
        propForSubscription(FETCH_USER_INTERVAL, FETCH_SUBSCRIPTION_RETRIES);
      },
    });
  };

  if (
    !(hasPlanFamily(permissions, 'discover') && !user.waitlisted) &&
    hasActivePermissions(permissions)
  ) {
    return <Redirect to="/" />;
  }

  return (
    <CardGroup>
      <Card
        className={classnames('p-5', styles.root, {
          'shadow-sm border-right-0': isUpsellConnectEnabled,
        })}
      >
        <div>
          <h4 className={classnames('mb-5 pageTitle')}>
            Subscription Management
          </h4>
          {hasNoLicenses && !loading ? (
            <SignUpSubscription
              onSubmit={handleCheckout}
              className={styles.subscriptionSignUp}
            />
          ) : (
            <SubscriptionManagement
              isLoading={loading}
              onManage={handleSubPortal}
            />
          )}
        </div>
      </Card>
      {isUpsellConnectEnabled && isConnectUser && (
        <SubscriptionUpsellEnterprise
          showButton={true}
          onManage={handleSubPortal}
        />
      )}
    </CardGroup>
  );
};

export default Subscription;
